import { format, formatISO, isValid, parseISO, subMonths } from 'date-fns';

export interface Option {
  label: string;
  value: {
    startDate: string;
    endDate: string;
  };
}
export interface OptionGroup {
  label: string;
  options: Option[];
}

export const formatDate = (date: Date | null): string => {
  return date ? format(date, 'MMMM d, yyyy') : '';
};
export const parseDate = (dateString: Date | string | null): Date | null => {
  return dateString ? new Date(dateString) : null;
};

export const calculatePreviousDates = (startDate: string, endDate: string) => {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const previousStart = subMonths(start, 1);
  const previousEnd = subMonths(end, 1);
  return {
    previousStartDate: formatISO(previousStart, { representation: 'complete' }),
    previousEndDate: formatISO(previousEnd, { representation: 'complete' }),
  };
};
export const getBillingPeriodOptions = (
  currentStartDate: string,
  currentEndDate: string
): OptionGroup[] => {
  const { previousStartDate, previousEndDate } = calculatePreviousDates(
    currentStartDate,
    currentEndDate
  );

  return [
    {
      label: 'Current Billing Period',
      options: [
        {
          label: `${formatDate(new Date(currentStartDate))} - ${formatDate(
            new Date(currentEndDate)
          )}`,
          value: { startDate: currentStartDate, endDate: currentEndDate },
        },
      ],
    },
    {
      label: 'Previous Billing Period',
      options: [
        {
          label: `${formatDate(new Date(previousStartDate))} - ${formatDate(
            new Date(previousEndDate)
          )}`,
          value: { startDate: previousStartDate, endDate: previousEndDate },
        },
      ],
    },
  ];
};

export const getDefaultOption = (
  options: OptionGroup[]
): Option | undefined => {
  const currentBillingPeriodGroup = options.find(
    group => group.label === 'Current Billing Period'
  );
  if (
    currentBillingPeriodGroup &&
    currentBillingPeriodGroup.options.length > 0
  ) {
    return currentBillingPeriodGroup.options[0];
  }
  return undefined;
};

export const formatDateRange = (startDate: string, endDate: string) =>
  `${startDate}_${endDate}`;
export const parseDateRange = (dateRange: string) => {
  if (!dateRange) return { startDate: null, endDate: null };
  const [startDate, endDate] = dateRange.split('_');
  return { startDate, endDate };
};
export const findMatchingOption = (
  startDate: string,
  endDate: string,
  options: any[]
) => {
  return options
    .flatMap(group => group.options)
    .find(
      option =>
        option.value.startDate === startDate && option.value.endDate === endDate
    );
};
export const validateAndDefaultDateRange = (
  dateRangeParam: any,
  options: OptionGroup[],
  setDateRangeParam: (arg0: string) => void
) => {
  const { startDate, endDate } = parseDateRange(dateRangeParam || '');
  const isValidDateRange =
    startDate &&
    endDate &&
    isValid(parseISO(startDate)) &&
    isValid(parseISO(endDate));
  if (isValidDateRange) {
    const validOption = findMatchingOption(startDate, endDate, options);

    if (validOption) {
      return validOption; // Return the valid option if found
    }
  }

  const defaultOption = getDefaultOption(options);
  if (defaultOption) {
    setDateRangeParam(
      formatDateRange(
        defaultOption.value.startDate,
        defaultOption.value.endDate
      )
    );
  }
  return defaultOption;
};

export const isSupportedBillingPlan = (billingPlanType: string) =>
  billingPlanType === 'INTERNAL' || billingPlanType === 'MONTHLY';
